import type { EvArtifact } from "../../types/agent-chat";
import { artifactSubtitle, artifactTitle } from "./artifactFormat";

export interface ArtifactCardProps {
  artifact: EvArtifact;
  /** True when this document is the one shown in the side panel. */
  isActive: boolean;
  onOpen: (artifact: EvArtifact) => void;
}

/**
 * Compact handle for a generated document inside an assistant turn.
 *
 * The document itself is rendered once, in the side panel; the message only
 * carries the model's summary and this card to (re)open the panel.
 */
export function ArtifactCard({ artifact, isActive, onOpen }: ArtifactCardProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen(artifact)}
      aria-pressed={isActive}
      className={`mt-3 flex w-full max-w-md items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
        isActive
          ? "border-brand-accent bg-brand-accent/10"
          : "border-brand-border bg-brand-bg hover:border-brand-accent/60"
      }`}
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-accent/10 text-brand-accent-dk"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M7 3h7l5 5v13H7z" />
          <path d="M14 3v5h5M9 13h6M9 17h6" />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-brand-text">
          {artifactTitle(artifact)}
        </span>
        <span className="block truncate text-xs text-brand-muted">{artifactSubtitle(artifact)}</span>
        {artifact.warnings.length > 0 && (
          <span className="mt-0.5 block text-[11px] text-brand-accent-dk">
            {artifact.warnings.length} avertissement{artifact.warnings.length > 1 ? "s" : ""}
          </span>
        )}
      </span>
      <span className="flex-shrink-0 text-xs font-semibold text-brand-accent-dk">
        {isActive ? "Affiché" : "Ouvrir"}
      </span>
    </button>
  );
}
